"use client";

import { useActionsTrack } from "@/_old/features/analytics";
import { useAuth } from "@/core/components/auth/store/auth";
import { DashboardSection } from "@/dashboard/components/dashboard-ds";
import { useDashboardFilters } from "@/dashboard/store/useDashboardFilters";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CheckInConfirmedBookings } from "./components/check-in-confirmed-bookings/check-in-confirmed-bookings";
import { DayUseBookings } from "./components/day-use-bookings/day-use-bookings";
import {
  ExceededCreditsCard,
  useExceededCreditsCard,
} from "./components/exceeded-credits-card";
import { ExcessConsumption } from "./components/excess-consumption/excess-consumption";
import { InsightsCardsConsumptionSkeleton } from "./components/insights-cards-consumption/insights-cards-consumption-skeleton";
import { InsightsConsumption } from "./components/insights-consumption/insights-consumption";

export type ExceededCardState = {
  visible: boolean;
  loading: boolean;
};

type DashboardInsightsProps = {
  onExceededCardStateChange?: (state: ExceededCardState) => void;
};

export function DashboardInsights({
  onExceededCardStateChange,
}: DashboardInsightsProps) {
  const { userInfo } = useAuth();
  const { onActionClick } = useActionsTrack();
  const companyId = userInfo?.league?.id;
  const companyName =
    userInfo?.league?.name ??
    userInfo?.league?.legal_name ??
    userInfo?.user?.company_name ??
    "";
  const userId = userInfo?.user?.id;
  const userEmail = userInfo?.profile?.email;
  const { filters } = useDashboardFilters();
  const [translate] = useTranslation(["dashboard"]);

  const exceededCardHookResult = useExceededCreditsCard({
    companyId: companyId ?? 0,
    companyName,
    userId,
    userEmail,
    onActionClick,
  });

  useEffect(() => {
    const visible = exceededCardHookResult.state !== "initial";
    const loading = exceededCardHookResult.isExceededCardDataLoading ?? false;
    onExceededCardStateChange?.({ visible, loading });
  }, [
    exceededCardHookResult.state,
    exceededCardHookResult.isExceededCardDataLoading,
    onExceededCardStateChange,
  ]);

  return (
    <DashboardSection
      title={translate("dashboard-insights-title")}
      data-testid="dashboard-insights"
    >
      {/* Figma 6036:3306: 2 cards por linha com gap de 20px; em telas
          estreitas (ou sidebar + painel do agente abertos) vira 1 coluna. */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {exceededCardHookResult.isExceededCardDataLoading ? (
          <InsightsCardsConsumptionSkeleton />
        ) : exceededCardHookResult.state !== "initial" ? (
          <ExceededCreditsCard
            companyId={companyId ?? 0}
            companyName={companyName}
            userId={userId}
            userEmail={userEmail}
            hookResult={exceededCardHookResult}
          />
        ) : (
          <ExcessConsumption
            startDate={filters.startDate}
            endDate={filters.endDate}
            companyId={companyId}
          />
        )}

        <InsightsConsumption
          startDate={filters.startDate}
          endDate={filters.endDate}
          companyId={companyId}
        />

        <CheckInConfirmedBookings
          startDate={filters.startDate}
          endDate={filters.endDate}
          companyId={companyId}
        />

        <DayUseBookings
          startDate={filters.startDate}
          endDate={filters.endDate}
          companyId={companyId}
        />
      </div>
    </DashboardSection>
  );
}
